import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * PLANOS — cria assinatura na Google Play + RevenueCat sem abrir 3 painéis.
 *
 * ⚠️ O QUE GOVERNA ESTA TELA: `productId` e `basePlanId` são PERMANENTES na Google — não dá
 * pra editar nem reusar. Por isso o botão de criar NÃO existe até a pessoa rodar a
 * conferência: primeiro `/preview` (que não grava nada e ainda checa se o id já está ocupado
 * na loja), e só então o "criar" aparece. É de propósito que dê mais trabalho.
 *
 * E depois de criar, o plano NÃO fica pronto: fica "propagando" (a loja leva até 24h). Só o
 * botão "Verificar" promove pra disponível, conferindo Google + RevenueCat de verdade.
 */

const ESTADOS = {
  rascunho: ["Rascunho", "gray", "Não foi publicado por este painel"],
  criando: ["Criando…", "orange", "Publicação em andamento"],
  propagando: ["Propagando", "orange", "Criado na loja — pode levar até 24h para ficar comprável"],
  disponivel: ["Disponível", "green", "Confirmado: está sendo servido para o app"],
  erro: ["Erro", "red", "Falhou no meio — dá para tentar de novo"],
};
const CORES = { green: "var(--green)", orange: "var(--orange)", red: "var(--cancel)", gray: "var(--text3)" };

const brl = (centavos) =>
  centavos == null
    ? "—"
    : `R$ ${(centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

const VAZIO = {
  productId: "",
  basePlanId: "",
  nome: "",
  preco: "",
  entitlements: "",
  offeringId: "default",
  packageId: "$rc_monthly",
  periodo: "P1M",
};

export default function Planos() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [conferencia, setConferencia] = useState(null); // resultado do /preview
  const [criando, setCriando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const carregar = useCallback(
    () => rodar(async () => setDados(await apiGet(API_ROUTES.ADMIN.plans()))),
    [rodar]
  );
  useEffect(() => {
    carregar();
  }, [carregar]);

  // Qualquer edição invalida a conferência: senão dava pra conferir um id e criar outro.
  const editar = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    setConferencia(null);
  };

  const centavos = () => Math.round(parseFloat(String(form.preco).replace(",", ".")) * 100);

  const corpo = () => ({
    productId: form.productId.trim(),
    basePlanId: form.basePlanId.trim(),
    nome: form.nome.trim(),
    centavos: centavos(),
    entitlements: form.entitlements.split(",").map((t) => t.trim()).filter(Boolean),
    offeringId: form.offeringId.trim(),
    packageId: form.packageId.trim(),
    periodo: form.periodo,
  });

  const conferir = () =>
    rodar(async () => {
      setAviso(null);
      setConferencia(await apiSend(API_ROUTES.ADMIN.plansPreview(), "POST", corpo()));
    });

  const criar = () =>
    rodar(async () => {
      setCriando(true);
      try {
        await apiSend(API_ROUTES.ADMIN.plans(), "POST", { ...corpo(), confirmado: true });
        setForm(VAZIO);
        setConferencia(null);
        setAviso("Plano criado. Ele está PROPAGANDO — use 'Verificar' daqui a alguns minutos.");
        await carregar();
      } finally {
        setCriando(false);
      }
    });

  const verificar = (id) =>
    rodar(async () => {
      const r = await apiSend(API_ROUTES.ADMIN.planVerify(id), "POST", {});
      setAviso(
        r.syncStatus === "disponivel"
          ? "Confirmado: o plano está disponível para compra."
          : "Ainda propagando. Isso é normal e pode levar até 24h — tente de novo mais tarde."
      );
      await carregar();
    });

  const desativar = (id, nome) =>
    rodar(async () => {
      if (!window.confirm(`Desativar "${nome}"?\n\nEle sai do catálogo, mas CONTINUA existindo na loja — os ids são permanentes e não podem ser apagados.`)) return;
      await apiSend(API_ROUTES.ADMIN.planDeactivate(id), "POST", {});
      await carregar();
    });

  const r = dados?.resumo;

  return (
    <>
      <AdminHeader titulo="Planos" tema={tema} setTema={setTema} />
      <Erro erro={erro} onFechar={limparErro} />

      {aviso && (
        <div style={{ ...cx.aviso, borderColor: "var(--green)" }}>
          {aviso}
          <button style={cx.x} onClick={() => setAviso(null)}>×</button>
        </div>
      )}

      {r && (
        <div style={cx.cards}>
          <Card n={r.total} label="No catálogo" />
          <Card n={r.disponiveis} label="Disponíveis" cor="var(--green)" />
          <Card n={r.propagando} label="Propagando" cor="var(--orange)" />
          <Card n={r.comErro} label="Com erro" cor="var(--cancel)" />
        </div>
      )}

      {/* ---------- criação ---------- */}
      <section style={cx.bloco}>
        <h3 style={cx.h3}>Criar plano na Google Play</h3>
        <p style={cx.aviso2}>
          ⚠️ <strong>O identificador é permanente.</strong> A Google não deixa editar nem reusar
          um <code>productId</code> — se errar, ele fica na loja para sempre. Por isso é
          obrigatório conferir antes.
        </p>

        <div style={cx.grid}>
          <Campo label="ID do produto" dica="minúsculas, números, _ ou ." valor={form.productId}
            onChange={(v) => editar("productId", v)} placeholder="reinoplay_desenhos_colorir" />
          <Campo label="ID do plano base" dica="minúsculas, números ou -" valor={form.basePlanId}
            onChange={(v) => editar("basePlanId", v)} placeholder="mensal" />
          <Campo label="Nome exibido" dica="é o que o cliente vê na loja" valor={form.nome}
            onChange={(v) => editar("nome", v)} placeholder="Desenhos para Colorir" />
          <Campo label="Preço (R$)" dica="use vírgula — ex.: 6,90" valor={form.preco}
            onChange={(v) => editar("preco", v)} placeholder="6,90" />
          <Campo label="Tags de acesso" dica="separadas por vírgula — é o que o plano libera"
            valor={form.entitlements} onChange={(v) => editar("entitlements", v)}
            placeholder="desenhos_colorir" />
          <Campo label="Offering / Package" dica="no RevenueCat" valor={form.offeringId}
            onChange={(v) => editar("offeringId", v)} placeholder="default" />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button style={cx.btn2} onClick={conferir}>1. Conferir</button>
          {/* O criar SÓ aparece depois de uma conferência válida — não é estilo, é trava. */}
          {conferencia?.valido && (
            <button style={cx.btn} onClick={criar} disabled={criando}>
              {criando ? "Criando…" : "2. Criar de verdade"}
            </button>
          )}
        </div>

        {conferencia && (
          <div style={{ ...cx.conf, borderColor: conferencia.valido ? "var(--green)" : "var(--cancel)" }}>
            {conferencia.valido ? (
              <>
                <strong style={{ color: "var(--green)" }}>✓ Pode criar</strong>
                <ul style={cx.ul}>
                  <li>Produto: <code>{conferencia.preview.google.productId}</code></li>
                  <li>Plano base: <code>{conferencia.preview.google.basePlanId}</code></li>
                  <li>Preço: <strong>{brl(centavos())}</strong> ({conferencia.preview.google.periodo})</li>
                  <li>Libera as tags: <code>{conferencia.preview.revenuecat.entitlements.join(", ")}</code></li>
                </ul>
                {conferencia.preview.avisos?.map((a, i) => (
                  <div key={i} style={cx.miniAviso}>⚠️ {a}</div>
                ))}
              </>
            ) : (
              <>
                <strong style={{ color: "var(--cancel)" }}>✕ Não dá para criar</strong>
                <ul style={cx.ul}>
                  {conferencia.problemas.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </>
            )}
            {conferencia.credenciais && (
              <div style={cx.cred}>
                Google Play: <b>{conferencia.credenciais.google}</b> · RevenueCat:{" "}
                <b>{conferencia.credenciais.revenuecat}</b>
                {/* App Store é informativo: não bloqueia criar plano no Android. */}
                {conferencia.credenciais.appstore && (
                  <> · App Store (iOS): <b>{conferencia.credenciais.appstore}</b></>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ---------- catálogo ---------- */}
      <section style={cx.bloco}>
        <h3 style={cx.h3}>Catálogo</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={cx.tabela}>
            <thead>
              <tr>
                {["Produto", "Origem", "Preço", "Libera", "Estado", ""].map((h) => (
                  <th key={h} style={cx.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(dados?.itens ?? []).map((p) => {
                const [rotulo, cor, explica] = ESTADOS[p.syncStatus] ?? [p.syncStatus, "gray", ""];
                return (
                  <tr key={p.id}>
                    <td style={cx.td}>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <code style={cx.code}>{p.productId}</code>
                    </td>
                    <td style={cx.td}>{p.source}</td>
                    <td style={cx.td}>{brl(p.priceCents)}</td>
                    <td style={cx.td}><code style={cx.code}>{(p.entitlements ?? []).join(", ")}</code></td>
                    <td style={cx.td}>
                      <span style={{ ...cx.pill, color: CORES[cor], borderColor: CORES[cor] }} title={explica}>
                        {rotulo}
                      </span>
                      {p.syncError && <div style={cx.erroLinha}>{p.syncError}</div>}
                    </td>
                    <td style={cx.td}>
                      {/* Só faz sentido verificar o que este painel publicou. */}
                      {["propagando", "erro", "criando"].includes(p.syncStatus) && (
                        <button style={cx.btnMini} onClick={() => verificar(p.id)}>Verificar</button>
                      )}
                      {p.active && (
                        <button style={cx.btnMiniOff} onClick={() => desativar(p.id, p.name)}>
                          Desativar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Card({ n, label, cor }) {
  return (
    <div style={cx.card}>
      <div style={{ fontSize: 26, fontWeight: 800, color: cor ?? "var(--text)" }}>{n}</div>
      <div style={{ fontSize: 12, color: "var(--text3)" }}>{label}</div>
    </div>
  );
}

/**
 * Campo do formulário.
 *
 * ⚠️ ALINHAMENTO: a linha da dica é SEMPRE renderizada (com espaço rígido quando não há
 * dica). Sem isso, os campos sem dica ficam uma linha mais altos que os demais e os inputs
 * saem do prumo — foi o que aconteceu na primeira versão desta tela.
 *
 * O `justifyContent: flex-end` (junto do `alignItems: end` do grid) é o cinto de segurança:
 * se uma dica quebrar em duas linhas numa tela estreita, o input continua rente à base e a
 * fileira segue alinhada.
 */
function Campo({ label, dica, valor, onChange, placeholder }) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        height: "100%",
        justifyContent: "flex-end",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.3, minHeight: 14 }}>
        {dica || " "}
      </span>
      <input style={cx.input} value={valor} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

const cx = {
  cards: { display: "flex", gap: 12, flexWrap: "wrap", margin: "0 0 18px" },
  card: { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 18px", minWidth: 120 },
  bloco: { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: 18, marginBottom: 18 },
  h3: { margin: "0 0 10px", fontSize: 16 },
  aviso2: { background: "rgba(255,140,0,.1)", border: "1px solid var(--orange)", borderRadius: 8, padding: "10px 12px", fontSize: 13, margin: "0 0 14px" },
  // `alignItems: end` mantém os inputs na mesma linha de base mesmo que um campo fique mais
  // alto que o outro (dica ausente ou quebrada em duas linhas). Ver o comentário em Campo().
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, alignItems: "end" },
  input: { padding: "9px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", fontSize: 14 },
  btn: { background: "var(--red)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, cursor: "pointer" },
  btn2: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 18px", fontWeight: 700, cursor: "pointer" },
  btnMini: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px", cursor: "pointer", marginRight: 6, fontSize: 12 },
  btnMiniOff: { background: "transparent", color: "var(--text3)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 },
  conf: { marginTop: 14, border: "1px solid", borderRadius: 8, padding: "12px 14px", fontSize: 13 },
  ul: { margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.7 },
  miniAviso: { marginTop: 8, fontSize: 12, color: "var(--orange)" },
  cred: { marginTop: 10, fontSize: 11, color: "var(--text3)" },
  aviso: { border: "1px solid", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10 },
  x: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18 },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--text3)", fontSize: 12, whiteSpace: "nowrap" },
  td: { padding: "10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" },
  code: { fontSize: 11, color: "var(--text3)" },
  pill: { border: "1px solid", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
  erroLinha: { fontSize: 11, color: "var(--cancel)", marginTop: 4, maxWidth: 260 },
};
